const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "");
  const value = parseInt(
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized,
    16
  );

  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
};

const toCssName = (value) =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/%/g, "")
    .toLowerCase();

const withAlpha = (hex, alphaValue) => `rgba(${hexToRgb(hex)}, ${alphaValue})`;

const primitiveColor = {
  neutral: {
    0: "#FFFFFF",
    50: "#FAFAFA",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172A",
    950: "#121212",
  },
  primary: {
    0: "#F5F3FF",
    50: "#EDE9FE",
    100: "#DDD6FE",
    200: "#C4B5FD",
    300: "#A78BFA",
    400: "#AF55EB",
    500: "#9645FF",
    550: "#744FCC",
    600: "#6D28D9",
    700: "#5B21B6",
    800: "#43277A",
    900: "#31237D",
    950: "#2D1B69",
  },
  success: {
    0: "#F0FDF4",
    50: "#DCFCE7",
    100: "#BBF7D0",
    200: "#86EFAC",
    300: "#4ADE80",
    400: "#22C55E",
    500: "#059669",
    600: "#15803D",
    700: "#166534",
    800: "#14532D",
    900: "#052E16",
  },
  warning: {
    0: "#FFFBEB",
    50: "#F9F8F6",
    100: "#F4F1E7",
    200: "#EDE2C5",
    300: "#E9D290",
    400: "#EEC554",
    500: "#F59E0B",
    600: "#CF8303",
    700: "#9A6100",
    800: "#583700",
    900: "#150D00",
  },
  error: {
    0: "#FEF2F2",
    50: "#FEE2E2",
    100: "#FECACA",
    200: "#FCA5A5",
    300: "#F87171",
    400: "#EF4444",
    500: "#DC2626",
    600: "#B91C1C",
    700: "#991B1B",
    800: "#7F1D1D",
    900: "#450A0A",
  },
  fuchsia: {
    50: "#F8F6F9",
    100: "#F1E7F3",
    200: "#E6C7EA",
    250: "#F79AFE",
    300: "#DA96E4",
    400: "#D35DE4",
    500: "#D946EF",
    600: "#D417F1",
    700: "#AE08C7",
    800: "#760587",
    900: "#3F0348",
  },
  cyan: {
    0: "#ECFEFF",
    50: "#F6F9F9",
    100: "#E7F2F3",
    200: "#C5E8EC",
    300: "#92DEE8",
    400: "#57D9EB",
    500: "#67E8F9",
    600: "#36E5FC",
    700: "#00DDFA",
    800: "#00A2B8",
    900: "#006875",
  },
};

const semanticColor = {
  background: {
    default: primitiveColor.neutral[0],
    subtle: primitiveColor.neutral[50],
    muted: primitiveColor.primary[0],
    inverse: primitiveColor.neutral[900],
    "subtle 50%": withAlpha(primitiveColor.neutral[100], 0.5),
  },
  foreground: {
    default: primitiveColor.neutral[950],
    muted: primitiveColor.neutral[500],
    subtle: primitiveColor.neutral[400],
    inverse: primitiveColor.neutral[0],
    "default 87%": withAlpha(primitiveColor.neutral[900], 0.87),
    "default 60%": withAlpha(primitiveColor.neutral[900], 0.6),
    "default 54%": withAlpha(primitiveColor.neutral[900], 0.54),
    "inverse 20%": withAlpha(primitiveColor.neutral[0], 0.2),
    "inverse 30%": withAlpha(primitiveColor.neutral[0], 0.3),
    "inverse 50%": withAlpha(primitiveColor.neutral[0], 0.5),
    "inverse 70%": withAlpha(primitiveColor.neutral[0], 0.7),
  },
  border: {
    default: primitiveColor.neutral[200],
    strong: primitiveColor.neutral[400],
    focus: primitiveColor.primary[500],
  },
  primary: {
    default: primitiveColor.primary[500],
    hover: primitiveColor.primary[600],
    active: primitiveColor.primary[700],
    subtle: primitiveColor.primary[0],
    foreground: primitiveColor.neutral[0],
  },
  destructive: {
    default: primitiveColor.error[500],
    hover: primitiveColor.error[600],
    active: primitiveColor.error[800],
    subtle: primitiveColor.error[0],
    foreground: primitiveColor.neutral[0],
    border: primitiveColor.error[200],
  },
  success: {
    default: primitiveColor.success[500],
    hover: primitiveColor.success[700],
    active: primitiveColor.success[800],
    subtle: primitiveColor.success[0],
    foreground: primitiveColor.neutral[0],
    "subtle 30%": withAlpha(primitiveColor.success[0], 0.3),
  },
  warning: {
    default: primitiveColor.warning[500],
    hover: primitiveColor.warning[600],
    active: primitiveColor.warning[700],
    subtle: primitiveColor.warning[0],
    foreground: primitiveColor.neutral[900],
  },
  muted: {
    default: primitiveColor.neutral[100],
    foreground: primitiveColor.neutral[500],
  },
  purple: {
    default: primitiveColor.fuchsia[500],
    subtle: primitiveColor.primary[0],
    foreground: primitiveColor.neutral[0],
  },
  sidebar: {
    "gradient-start": primitiveColor.primary[900],
    "gradient-mid": primitiveColor.primary[700],
    "gradient-end": primitiveColor.primary[950],
    "page-selected-stroke-1": primitiveColor.primary[400],
    "page-selected-stroke-2": primitiveColor.fuchsia[250],
    "page-selected-stroke-3": primitiveColor.primary[500],
    "page-selected-stroke-4": primitiveColor.primary[600],
    "page-selected-fill-1": withAlpha(primitiveColor.fuchsia[500], 0.4),
    "page-selected-fill-2": withAlpha(primitiveColor.primary[600], 0.4),
    "page-selected-fill-3": withAlpha(primitiveColor.primary[500], 0.2),
    "page-selected-fill-4": withAlpha(primitiveColor.fuchsia[500], 0.6),
  },
  cyan: {
    default: primitiveColor.cyan[500],
    subtle: primitiveColor.cyan[0],
  },
  mint: {
    default: primitiveColor.success[200],
    subtle: primitiveColor.success[0],
  },
  fuchsia: {
    default: primitiveColor.fuchsia[500],
    subtle: primitiveColor.primary[0],
  },
  amber: {
    default: primitiveColor.warning[500],
  },
  softYellow: {
    default: primitiveColor.warning[400],
  },
  table: {
    header: primitiveColor.primary[0],
    row1: withAlpha(primitiveColor.primary[0], 0.5),
    row2: primitiveColor.neutral[0],
  },
  brandGradient: {
    "start 30%": withAlpha(primitiveColor.primary[900], 0.3),
  },
};

const appColor = {
  info: {
    subtle: "#EFF6FF",
    default: "#2563EB",
    foreground: primitiveColor.neutral[0],
  },
  shellSidebar: {
    navLabelMuted: "#D5C9EE",
    navItemLogoutHoverBg: "rgba(255, 255, 255, 0.08)",
  },
};

export const uiTokens = {
  color: {
    primitive: primitiveColor,
    semantic: semanticColor,
    app: appColor,
  },
  component: {
    button: {
      paddingX: {
        sm: "0.75rem",
        md: "1rem",
      },
    },
    shellSidebar: {
      headerUnderlineWidth: "168px",
      headerUnderlineRadius: "1px",
    },
  },
  fontWeight: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  typography: {
    display: {
      "2xl": {
        fontSize: "4.5rem",
        fontWeight: 700,
        lineHeight: "5.625rem",
      },
      xl: {
        fontSize: "3.75rem",
        fontWeight: 700,
        lineHeight: "4.5rem",
      },
      lg: {
        fontSize: "3rem",
        fontWeight: 600,
        lineHeight: "3.75rem",
      },
      md: {
        fontSize: "2.25rem",
        fontWeight: 600,
        lineHeight: "2.75rem",
      },
      sm: {
        fontSize: "1.875rem",
        fontWeight: 600,
        lineHeight: "2.375rem",
      },
    },
    heading: {
      xl: {
        fontSize: "1.5rem", // 24px
        fontWeight: 600,
        lineHeight: "2rem",
      },
      lg: {
        fontSize: "1.25rem", // 20px
        fontWeight: 600,
        lineHeight: "1.75rem",
      },
      md: {
        fontSize: "1.125rem", // 18px
        fontWeight: 600,
        lineHeight: "1.75rem",
      },
      sm: {
        fontSize: "1rem", // 16px
        fontWeight: 600,
        lineHeight: "1.5rem",
      },
      xs: {
        fontSize: "0.875rem", // 14px
        fontWeight: 600,
        lineHeight: "1.25rem",
      },
      xxs: {
        fontSize: "0.75rem", // 12px
        fontWeight: 600,
        lineHeight: "1rem",
      },
    },
    body: {
      lg: {
        fontSize: "1.125rem", // 18px
        fontWeight: 400,
        lineHeight: "1.75rem",
      },
      md: {
        fontSize: "1rem", // 16px
        fontWeight: 400,
        lineHeight: "1.5rem",
      },
      sm: {
        fontSize: "0.875rem", // 14px
        fontWeight: 400,
        lineHeight: "1.25rem",
      },
      xs: {
        fontSize: "0.75rem", // 12px
        fontWeight: 400,
        lineHeight: "1rem",
      },
      xxs: {
        fontSize: "0.625rem", // 10px
        fontWeight: 400,
        lineHeight: "0.875rem",
      },
    },
    label: {
      lg: {
        fontSize: "1rem", // 16px
        fontWeight: 500,
        lineHeight: "1.5rem",
      },
      md: {
        fontSize: "0.875rem", // 14px
        fontWeight: 500,
        lineHeight: "1.25rem",
      },
      sm: {
        fontSize: "0.75rem", // 12px
        fontWeight: 500,
        lineHeight: "1rem",
      },
      xs: {
        fontSize: "10px",
        fontWeight: 500,
        lineHeight: "0.9375rem",
        letterSpacing: 0,
      },
      xxs: {
        fontSize: "9px",
        fontWeight: 500,
        lineHeight: "0.9375rem",
        letterSpacing: 0,
      },
    },
  },
  space: {
    0: "0rem",
    2: "0.125rem",
    4: "0.25rem",
    6: "0.375rem",
    8: "0.5rem",
    10: "0.625rem",
    12: "0.75rem",
    14: "0.875rem",
    16: "1rem",
    18: "1.125rem",
    20: "1.25rem",
    22: "1.375rem",
    24: "1.5rem",
    26: "1.625rem",
    28: "1.75rem",
    30: "1.875rem",
    32: "2rem",
    34: "2.125rem",
    36: "2.25rem",
    38: "2.375rem",
    40: "2.5rem",
    42: "2.625rem",
    44: "2.75rem",
    46: "2.875rem",
    48: "3rem",
    50: "3.125rem",
    52: "3.25rem",
    56: "3.5rem",
    64: "4rem",
    72: "4.5rem",
    80: "5rem",
    96: "6rem",
    112: "7rem",
  },
  radius: {
    none: "0px",
    sm: "2px",
    md: "4px",
    lg: "8px",
    xl: "12px",
    "2xl": "16px",
    "3xl": "24px",
    full: "999999px",
  },
  shadow: {
    sm: "0 1px 2px 0px rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.10)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.10)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.10)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  breakpoint: {
    sm: "40rem", // 640px
    md: "48rem", // 768px
    lg: "64rem", // 1024px
    xl: "80rem", // 1280px
    "2xl": "96rem", // 1536px
  },
};

/**
 * Figma semantic tokens → injected as `var(--color-{key})` on `:root` (see `createCssVariables`).
 * Semantic aliases are generated from `uiTokens.color.semantic`.
 * @type {Record<string, ReadonlyArray<string | number>>}
 */
const collectColorAliases = (node, path = []) =>
  Object.entries(node).reduce((aliases, [key, value]) => {
    const nextPath = [...path, key];
    if (typeof value === "string") {
      aliases[nextPath.map(toCssName).join("-")] = ["semantic", ...nextPath];
      return aliases;
    }

    return {
      ...aliases,
      ...collectColorAliases(value, nextPath),
    };
  }, {});

export const FIGMA_SEMANTIC_COLOR_ALIASES = {
  ...collectColorAliases(uiTokens.color.semantic),
  "error-default": ["semantic", "destructive", "default"],
  "info-default": ["app", "info", "default"],
};

/**
 * @param {ReadonlyArray<string | number>} pathSegments
 * @returns {string | null} hex or other color string at path
 */
export const resolveUiColorPath = (pathSegments) => {
  let node = uiTokens.color;
  for (const seg of pathSegments) {
    if (node == null || typeof node !== "object") return null;
    node = node[seg];
  }
  return typeof node === "string" ? node : null;
};

export const getUiColorRgb = (...pathSegments) => {
  const value = resolveUiColorPath(pathSegments);
  return value?.startsWith("#") ? hexToRgb(value) : null;
};

export const remToPx = (value) => Number.parseFloat(value) * 16;

export const createCssVariables = () => {
  const cssVariables = {};

  const addColorVariables = (path, value) => {
    if (typeof value === "string") {
      const variableName = `--color-${path.map(toCssName).join("-")}`;
      cssVariables[variableName] = value;
      if (value.startsWith("#")) {
        cssVariables[`${variableName}-rgb`] = hexToRgb(value);
      }
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      addColorVariables([...path, key], nestedValue);
    });
  };

  addColorVariables([], uiTokens.color.primitive);
  addColorVariables([], uiTokens.color.semantic);
  addColorVariables([], uiTokens.color.app);

  Object.entries(FIGMA_SEMANTIC_COLOR_ALIASES).forEach(([aliasKey, path]) => {
    const value = resolveUiColorPath(path);
    if (typeof value !== "string" || value.length === 0) return;
    const varName = `--color-${aliasKey}`;
    cssVariables[varName] = value;
    if (value.startsWith("#")) {
      cssVariables[`${varName}-rgb`] = hexToRgb(value);
    }
  });

  Object.entries(uiTokens.space).forEach(([key, value]) => {
    cssVariables[`--space-${key}`] = value;
  });

  Object.entries(uiTokens.radius).forEach(([key, value]) => {
    cssVariables[`--rounded-${key}`] = value;
  });

  Object.values(uiTokens.fontWeight).forEach((value) => {
    cssVariables[`--font-weight-${value}`] = value;
  });

  Object.entries(uiTokens.typography).forEach(([group, sizes]) => {
    Object.entries(sizes).forEach(([size, styles]) => {
      Object.entries(styles).forEach(([property, value]) => {
        if (property === "fontWeight") return;
        cssVariables[
          `--text-${group}-${toCssName(size)}-${toCssName(property)}`
        ] = value;
      });
      if (styles.letterSpacing == null) {
        cssVariables[`--text-${group}-${toCssName(size)}-letter-spacing`] = 0;
      }
    });
  });

  Object.entries(uiTokens.shadow).forEach(([key, value]) => {
    cssVariables[`--shadow-${key}`] = value;
  });

  Object.entries(uiTokens.breakpoint).forEach(([key, value]) => {
    cssVariables[`--breakpoint-${key}`] = value;
  });

  const addComponentVariables = (path, value) => {
    if (typeof value === "string" || typeof value === "number") {
      cssVariables[`--component-${path.map(toCssName).join("-")}`] = value;
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      addComponentVariables([...path, key], nestedValue);
    });
  };

  addComponentVariables([], uiTokens.component);

  return cssVariables;
};
